import React, { useState, useEffect } from "react";
import LoaderSpiner from "../LoaderSpiner";
import { useTheme } from "../BrandColors/Context/ThemeContext";
import { getBackendBaseUrl } from "../../utils/apiUrl";

const AdminDealershipList = () => {
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Items per page
  const adminTheme = useTheme() || "#3b82f6";

  const API_URL = `${getBackendBaseUrl()}/dealerships`;

  useEffect(() => {
    async function fetchDeals() {
      setLoading(true);
      setError(""); // Reset error state before fetching
      try {
        const token = localStorage.getItem("authToken"); // Replace with your token retrieval logic
        const response = await fetch(API_URL, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Add the token here
          },
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setDealerships(data.data);
      } catch (error) {
        console.error("Error fetching dealerships:", error.message);
        setError("Failed to fetch dealerships. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchDeals();
  }, []);

  // Calculate current dealerships for the page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDealerships = dealerships.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(dealerships.length / itemsPerPage);

  return (
    <div className="glass-panel w-full flex flex-col p-6 animate-fade-in-up mt-8">
      <h2 className="text-2xl font-bold text-white mb-6">List of Dealerships</h2>

      {/* Loading and Error States */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <LoaderSpiner />
        </div>
      )}
      {error && <p className="text-red-400 font-medium bg-red-900/20 p-4 rounded-lg">{error}</p>}

      {/* Empty State */}
      {!loading && !error && dealerships.length === 0 && (
        <div className="text-center py-12 bg-white/30 rounded-lg border border-blue-200/50">
           <p className="text-slate-600">No dealerships available.</p>
        </div>
      )}

      {/* Dealership Table */}
      {!loading && !error && dealerships.length > 0 && (
        <div className="w-full overflow-x-auto rounded-lg border border-blue-200/50 scrollbar-hide">
          <table className="w-full text-left border-collapse min-w-max">
            <thead>
              <tr className="text-white text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: adminTheme }}>
                <th className="px-4 py-4 rounded-tl-lg">No</th>
                <th className="px-4 py-4">Name</th>
                <th className="px-4 py-4">Email</th>
                <th className="px-4 py-4">Created At</th>
                <th className="px-4 py-4">Assigned Modules</th>
                <th className="px-4 py-4 rounded-tr-lg">Assigned Products</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 bg-white/30">
              {currentDealerships.map((dealership, index) => (
                <tr key={dealership._id} className="text-slate-700 text-sm hover:bg-white/5 transition-colors group">
                  <td className="px-4 py-4">{indexOfFirstItem + index + 1}</td>
                  <td className="px-4 py-4 font-medium text-slate-800">{dealership?.dealershipId?.dealershipName || dealership.name || "N/A"}</td>
                  <td className="px-4 py-4">{dealership.email || "N/A"}</td>
                  <td className="px-4 py-4">{new Date(dealership.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-4">
                    {dealership.assignedModules.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {dealership.assignedModules.map((module) => (
                          <li key={module._id} className="text-slate-600 group-hover:text-slate-700">
                            {module?.module?.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-slate-500 italic">No modules assigned</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {dealership.assignedProducts.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1">
                        {dealership.assignedProducts &&
                          dealership.assignedProducts.map((product) => (
                            <li key={product._id} className="text-slate-600 group-hover:text-slate-700">
                              {product?.product?.name}
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <span className="text-slate-500 italic">No products assigned</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-2">
           <div className="flex gap-1">
            {[...Array(totalPages).keys()].map((number) => (
              <button
                key={number}
                onClick={() => paginate(number + 1)}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm transition-all ${
                  currentPage === number + 1 
                    ? 'text-white shadow-lg scale-105'
                    : 'bg-white text-slate-600 hover:bg-blue-100 hover:text-white'
                }`}
                style={currentPage === number + 1 ? { backgroundColor: adminTheme } : {}}
              >
                {number + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDealershipList;
